import React, { useState, useEffect, useCallback, useRef } from 'react';
import useModalLock from '../../utils/useModalLock';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Clock, Upload, ExternalLink,
  ChevronDown, ChevronUp, ZoomIn, ZoomOut, FileText, Edit, Save, X,
  Trophy, Link2, ChevronLeft, ChevronRight, AlertTriangle, Info,
  UploadCloud, MousePointerClick, History
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';
import Sidebar from '../../components/admin/Sidebar';
import Header from '../../components/admin/Header';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime, STAGES, ROLES, CHECKLIST_ITEMS, canUserApprove, getRoleBadgeClass, getApprovalCardDisplayState, shouldShowReminderAction } from '../../utils/helpers';
import { buildReminderRequest } from '../../utils/reminderPayload';
import {
  DEFAULT_ARTWORK_LIST_TAB,
  getStoredArtworkListTab,
} from './artworkListTabStorage';
import './ArtworkDetail.css';
import './ArtworkDetail.revamp.css';

// Set up PDF.js worker - use the path that works with create-react-app build
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const STAGE_LABELS = {
  1: { title: 'Stage 1: Pre-NAFDAC Approval', desc: 'Initial artwork review before NAFDAC submission' },
  2: { title: 'Stage 2: Pre-Supplier Approval', desc: 'Final artwork sign-off before sharing with supplier' },
  3: { title: 'Stage 3: Position Proof', desc: 'Supplier position proof review and approval' },
  4: { title: 'Stage 4: Colour Proof / Chromalin', desc: 'Final colour proof acknowledgement (hard copy)' },
};

const SUPPLIER_COLUMN_PALETTE = [
  { bg: 'rgba(219, 234, 254, 0.65)', border: 'rgba(59, 130, 246, 0.45)' },
  { bg: 'rgba(220, 252, 231, 0.65)', border: 'rgba(34, 197, 94, 0.45)' },
  { bg: 'rgba(255, 247, 237, 0.65)', border: 'rgba(249, 115, 22, 0.45)' },
  { bg: 'rgba(237, 233, 254, 0.65)', border: 'rgba(124, 58, 237, 0.45)' },
  { bg: 'rgba(232, 244, 255, 0.65)', border: 'rgba(59, 130, 246, 0.35)' },
];

const OPTIONAL_REVIEW_ROLES = ['coo', 'marketing'];

function getAssignedReviewerName(approval) {
  return approval.assignedUserName || approval.userName || ROLES[approval.role]?.label || approval.role;
}

function uniqueReviewerNames(approvals) {
  return [...new Set(approvals.map(getAssignedReviewerName).filter(Boolean))];
}

function getPendingReviewersForStage(stageData) {
  const approvals = stageData?.approvals || [];
  if (!approvals.length || stageData?.status !== 'in_progress') return [];

  // Roles ordered by review priority
  const rolesPriority = ['rd', 'regulatory', 'qa', ...OPTIONAL_REVIEW_ROLES, 'cco'];

  const pendingByRole = rolesPriority.flatMap(role => approvals.filter(a => a.role === role && a.status === 'pending'));
  const otherPending = approvals.filter(a => !rolesPriority.includes(a.role) && a.status === 'pending');

  const combined = [...pendingByRole, ...otherPending];
  return uniqueReviewerNames(combined);
}

function StageProgress({ currentStage, artwork }) {
  return (
    <div className="stage-progress-wrap">
      {[1,2,3,4].map((s, i) => {
        const stageData = artwork[`stage${s}`];
        const isDone = stageData?.status === 'completed';
        const isActive = s === currentStage;
        const isChanges = stageData?.status === 'changes_requested';
        return (
          <React.Fragment key={s}>
            <div className="stage-step">
              <div className={`stage-step-circle ${isDone ? 'done' : isActive ? 'active' : ''} ${isChanges ? 'changes' : ''}`}>
                {isDone ? <CheckCircle size={16} /> : s}
              </div>
              <span className={`stage-step-label ${isDone ? 'done' : isActive ? 'active' : ''}`}>
                {STAGES[s].short}
              </span>
            </div>
            {i < 3 && <div className={`stage-connector ${isDone || (s < currentStage) ? 'done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ApprovalCard({ approval, role, onClick, currentUserRole, stageData, showReminderButton, onSendReminder, reminderSubmitting, stageNum, supplierSubmissionsMissing }) {
  const statusConfig = {
    pending: { icon: <Clock size={14} />, cls: 'badge-gray', lbl: 'Pending' },
    approved: { icon: <CheckCircle size={14} />, cls: 'badge-success', lbl: 'Approved' },
    changes_requested: { icon: <XCircle size={14} />, cls: 'badge-danger', lbl: 'Changes needed' },
  };
  const resolvedRole = role || approval?.role || approval?.assignedUserRole || approval?.reviewerRole || approval?.assignedUser?.role || 'reviewer';
  const pendingDisplayState = approval.status === 'pending'
    ? getApprovalCardDisplayState(approval, stageData, currentUserRole)
    : null;
  const cfg = approval.status === 'pending' && pendingDisplayState
    ? { icon: <Clock size={14} />, cls: pendingDisplayState.badge, lbl: pendingDisplayState.label }
    : statusConfig[approval.status] || statusConfig.pending;
  // If stage 3/4 is missing this supplier's submission, do not show 'Awaiting Review' —
  // indicate we're waiting for the submission instead.
  if (approval.status === 'pending' && supplierSubmissionsMissing && (stageNum === 3 || stageNum === 4)) {
    cfg.icon = <Clock size={14} />;
    cfg.cls = 'badge-gray';
    cfg.lbl = 'Waiting for supplier submission';
  }
  const roleLabel = ROLES[resolvedRole]?.label || (typeof resolvedRole === 'string' ? resolvedRole.replace(/_/g, ' ') : 'Reviewer');
  // Admin can click any approval card, but other roles can only click their own
  const canClick = (currentUserRole === 'admin') || (resolvedRole === currentUserRole);
  const isClickable = canClick && approval.status !== 'pending' && approval.checklist && approval.checklist.length > 0;

  const reviewerName = approval.assignedUserName || approval.userName || approval.assignedUserEmail || 'Pending assignment';

  return (
    <div
      className={`approval-card ${approval.status}`}
      style={{ cursor: isClickable ? 'pointer' : 'default' }}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : -1}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {showReminderButton && (
        <div style={{display:'flex', justifyContent:'flex-end', marginBottom:6}}>
          <button
            className="btn btn-secondary btn-sm"
            style={{padding:'6px 10px', fontSize:'0.72rem', borderRadius:'999px', whiteSpace:'nowrap', fontWeight:700, boxShadow:'0 1px 2px rgba(15, 23, 42, 0.08)'}}
            onClick={(e) => {
              e.stopPropagation();
              onSendReminder?.();
            }}
            disabled={reminderSubmitting}
          >
            {reminderSubmitting ? <span className="spinner" /> : 'Remind'}
          </button>
        </div>
      )}
      <div className="approval-card-header">
        <span className={`badge ${getRoleBadgeClass(resolvedRole)}`}>{roleLabel}</span>
        <span className={`badge ${cfg.cls} approval-status-badge`}>
          {cfg.icon} {cfg.lbl}
        </span>
      </div>
      {reviewerName && <p className="approval-by">{reviewerName}</p>}
      {approval.signedAt && <p className="approval-date">{formatDateTime(approval.signedAt)}</p>}
      {approval.comments && <p className="approval-comments">{approval.comments}</p>}
    </div>
  );
}

function ChecklistTable({ checklist, onChange, readOnly }) {
  return (
    <div className="checklist-table-wrap">
      <table className="checklist-table">
        <thead>
          <tr>
            <th>S/N</th>
            <th>Item</th>
            <th className="tick-cell">Done</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          {checklist.map((row, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{row.item}</td>
              <td className="tick-cell">
                {readOnly
                  ? (row.ticks?.[0]
                      ? <span className="tick-readonly"><CheckCircle size={16} /></span>
                      : <span className="tick-empty"><X size={16} /></span>)
                  : <input type="checkbox" checked={!!row.ticks?.[0]}
                      onChange={e => {
                        const updated = [...checklist];
                        const newTicks = [e.target.checked];
                        updated[i] = {...row, ticks: newTicks};
                        onChange(updated);
                      }} />
                }
              </td>
              <td>
                {readOnly
                  ? (row.comments?.[0] || '—')
                  : <input className="form-input" style={{padding:'4px 8px',fontSize:'0.8125rem'}}
                      placeholder="Add comment…"
                      value={row.comments?.[0] || ''}
                      onChange={e => {
                        const updated = [...checklist];
                        updated[i] = {...row, comments: [e.target.value]};
                        onChange(updated);
                      }} />
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ViewChecklistModal({ approval, role, stageNum, onClose }) {
  const statusConfig = {
    pending: { icon: <Clock size={16} />, cls: 'badge-gray', lbl: 'Pending' },
    approved: { icon: <CheckCircle size={16} />, cls: 'badge-success', lbl: 'Approved' },
    changes_requested: { icon: <XCircle size={16} />, cls: 'badge-danger', lbl: 'Changes needed' },
  };
  const cfg = statusConfig[approval.status] || statusConfig.pending;

  useModalLock();

  const reviewerRoleLabel = approval.teamLevel === 'head'
    ? 'Head'
    : approval.teamLevel === 'member'
      ? 'Team member'
      : null;

  const reviewerDisplayName = approval.userName || approval.assignedUserName || '—';

  return (
    <div className="modal-overlay">
      <div className="modal modal-xl">
        <div className="modal-header">
          <div>
            <h3 style={{fontSize:'1.0625rem', fontWeight:700}}>Team review details</h3>
            <p style={{fontSize:'0.8125rem', color:'var(--gray-500)', marginTop:2}}>
              Stage {stageNum}: {STAGES[stageNum]?.label} · {ROLES[role]?.label}
            </p>
            <div style={{display:'flex', gap:24, fontSize:'0.8125rem', color:'var(--gray-600)', marginTop:8}}>
              <div>
                <span style={{fontWeight:600}}>Reviewer:</span> {reviewerDisplayName}
                {reviewerRoleLabel && (
                  <span style={{color:'var(--gray-500)', marginLeft:6}}>({reviewerRoleLabel})</span>
                )}
              </div>
              {approval.signedAt && (
                <div><span style={{fontWeight:600}}>Date:</span> {formatDateTime(approval.signedAt)}</div>
              )}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body" style={{maxHeight:'60vh', overflowY:'auto'}}>
          {/* Status Summary */}
          <div style={{marginBottom:24, padding:12, background:'var(--gray-50)', borderRadius:'var(--radius-md)', border:'1px solid var(--gray-200)'}}>
            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
              <span className={`badge ${cfg.cls} approval-status-badge`}>
                {cfg.icon} {cfg.lbl}
              </span>
            </div>
            {approval.comments && (
              <div>
                <p style={{fontSize:'0.8125rem', fontWeight:600, color:'var(--gray-600)', marginBottom:6}}>Comments</p>
                <p style={{fontSize:'0.8125rem', color:'var(--gray-700)', lineHeight:1.5, fontStyle:'italic', margin:0}}>
                  {approval.comments}
                </p>
              </div>
            )}
          </div>

          {/* Checklist */}
          {approval.checklist && approval.checklist.length > 0 ? (
            <div>
              <p style={{fontWeight:600, fontSize:'0.875rem', marginBottom:12}}>Checklist</p>
              <ChecklistTable checklist={approval.checklist} onChange={() => {}} readOnly={true} />
            </div>
          ) : (
            <div style={{padding:24, textAlign:'center', color:'var(--gray-500)'}}>
              <p style={{fontSize:'0.875rem'}}>No checklist data available</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function SupplierTriggerBar({ artwork, onAssign }) {
  const [isOpen, setIsOpen] = useState(false);
  const stage2Complete = artwork.stage2?.status === 'completed';
  const assignedSuppliers = artwork.supplierAssignmentInfo?.assignedSuppliers || [];
  const hasAssignedSuppliers = assignedSuppliers.length > 0;
  const assignedSupplierNames = assignedSuppliers.map(s => s?.name || s).filter(Boolean);

  return (
    <>
      <div className="info-banner"
        style={{
          marginBottom:16,
          justifyContent:'space-between',
          alignItems:'center',
          background: stage2Complete ? 'var(--success-light)' : 'var(--info-light)',
          border: `1px solid ${stage2Complete ? 'var(--success)' : 'var(--info)'}`,
        }}>
        <div style={{display:'flex', alignItems:'flex-start', gap:10}}>
          {stage2Complete ? <CheckCircle size={18} color="var(--success)" style={{marginTop:1}} /> : <Clock size={18} color="var(--info)" style={{marginTop:1}} />}
          <div>
            <p className="info-banner-title" style={{color: stage2Complete ? 'var(--success)' : 'var(--info)'}}>
              {hasAssignedSuppliers ? `Assigned to ${assignedSupplierNames.join(', ')}` : 'Stages 1 & 2 complete'}
            </p>
            <p className="info-banner-body">
              {hasAssignedSuppliers
                ? `This artwork is already assigned to: ${assignedSupplierNames.join(', ')}. Add more suppliers for Stage 3 (Position Proof).`
                : 'Ready to assign this artwork to suppliers for Stage 3 (Position Proof).'}
            </p>
          </div>
        </div>
        <button
          className="btn btn-success btn-sm"
          onClick={() => setIsOpen(true)}
          disabled={!stage2Complete}
          title={!stage2Complete ? 'Complete Stage 2 approvals first' : ''}
          style={{opacity: !stage2Complete ? 0.5 : 1, cursor: !stage2Complete ? 'not-allowed' : 'pointer', flexShrink:0}}
        >
          {hasAssignedSuppliers ? 'Assign more suppliers' : 'Assign to suppliers'}
        </button>
      </div>

      {isOpen && (
        <SupplierSelectionModal
          artwork={artwork}
          onClose={() => setIsOpen(false)}
          onAssign={onAssign}
        />
      )}
    </>
  );
}

function SupplierSelectionModal({ artwork, onClose, onAssign }) {
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const currentAssignedSuppliers = React.useMemo(
    () => artwork.supplierAssignmentInfo?.assignedSuppliers || [],
    [artwork.supplierAssignmentInfo]
  );
  const currentAssignedSupplierNames = React.useMemo(
    () => currentAssignedSuppliers
      .map((supplier) => supplier?.name || supplier)
      .filter(Boolean),
    [currentAssignedSuppliers]
  );
  const currentAssignedSupplierIds = React.useMemo(
    () => currentAssignedSuppliers
      .map((supplier) => String(supplier._id || supplier))
      .filter(Boolean),
    [currentAssignedSuppliers]
  );

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await api.get('/suppliers');
        setSuppliers(res.data.filter(s => s.isActivated && !currentAssignedSupplierIds.includes(String(s._id))));
      } catch (err) {
        toast.error('Failed to load suppliers');
      } finally {
        setLoading(false);
      }
    };
    fetchSuppliers();
  }, [currentAssignedSupplierIds]);

  const handleAssign = async () => {
    if (selectedSuppliers.length === 0) {
      toast.error('Please select at least one supplier');
      return;
    }

    setAssigning(true);
    try {
      await api.post(`/artworks/${artwork._id}/assign-suppliers`, {
        artworkId: artwork._id,
        supplierIds: selectedSuppliers
      });
      toast.success('Suppliers assigned and notified successfully!');
      onAssign();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign suppliers');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
      <div style={{background:'white', borderRadius:'var(--radius-lg)', padding:'24px', maxWidth:'500px', width:'90%', maxHeight:'80vh', overflowY:'auto'}}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16}}>
          <h2 style={{fontSize:'1.25rem', fontWeight:700, margin:0}}>Assign to suppliers</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>

        <p style={{fontSize:'0.875rem', color:'var(--gray-600)', marginBottom:16}}>
          Select one or more suppliers to receive this artwork:
        </p>

        {loading ? (
          <div style={{textAlign:'center', padding:'40px 0'}}>
            <div className="spinner spinner-dark" style={{width:32, height:32}} />
          </div>
        ) : suppliers.length === 0 ? (
          <div style={{padding:'20px', textAlign:'center'}}>
            <div style={{marginBottom:8, padding:12, background:'#fff4e5', border:'1px solid #f5d7a7', borderRadius:'8px', color:'#8a6d3b'}}>
              <strong>All activated suppliers are already assigned.</strong>
              <p style={{margin:'8px 0 0', fontSize:'0.85rem', color:'#735a3d'}}>There are no additional active suppliers available to add for this artwork.</p>
            </div>
          </div>
        ) : (
          <>
            {currentAssignedSupplierNames.length > 0 && (
              <div style={{marginBottom:12, fontSize:'0.85rem', color:'var(--gray-600)'}}>
                <strong>Currently assigned:</strong> {currentAssignedSupplierNames.join(', ')}
              </div>
            )}
            <div style={{marginBottom:20}}>
              {suppliers.map(supplier => (
                <label key={supplier._id} style={{display:'flex', alignItems:'center', padding:'12px', border:'1px solid var(--gray-200)', borderRadius:'var(--radius-md)', marginBottom:8, cursor:'pointer', transition:'all 0.2s', background: selectedSuppliers.includes(supplier._id) ? 'var(--info-light)' : 'white'}}>
                  <input
                    type="checkbox"
                    checked={selectedSuppliers.includes(supplier._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSuppliers([...selectedSuppliers, supplier._id]);
                      } else {
                        setSelectedSuppliers(selectedSuppliers.filter(id => id !== supplier._id));
                      }
                    }}
                    style={{marginRight:12, width:18, height:18, cursor:'pointer'}}
                  />
                  <div style={{flex:1}}>
                    <p style={{fontWeight:600, fontSize:'0.875rem', margin:0}}>{supplier.name}</p>
                    <p style={{fontSize:'0.75rem', color:'var(--gray-500)', margin:'2px 0 0 0'}}>
                      {[supplier.email, ...(supplier.emails || [])]
                        .filter(Boolean)
                        .map((email) => String(email).trim())
                        .filter((email, index, arr) => arr.indexOf(email) === index)
                        .join(', ')}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        <div style={{display:'flex', gap:12, justifyContent:'flex-end'}}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-success"
            onClick={handleAssign}
            disabled={assigning || selectedSuppliers.length === 0}
          >
            {assigning ? <span className="spinner" /> : <><CheckCircle size={15} /> Assign suppliers</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApprovalModal({ artwork, stageNum, supplierId, onClose, onSubmit }) {
  const { user } = useAuth();
  const [zoom, setZoom] = useState(100);
  const [checklist, setChecklist] = useState(
    CHECKLIST_ITEMS.map(item => ({ item, ticks: [], comments: [] }))
  );
  const [confirmAction, setConfirmAction] = useState(null); // 'approved' | 'changes_requested' | null
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState(0); // Track which file is selected
  const imageContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null); // Track PDF render task to prevent concurrent renders

  const stageData = artwork[`stage${stageNum}`];
  // For Stage 3 / Stage 4, the file being reviewed lives on the specific
  // supplier's submission, not on the stage root.
  const submissionData = (stageNum === 3 || stageNum === 4) && supplierId
    ? (stageData?.submissions || []).find(s => String((s.supplierId && s.supplierId._id) || s.supplierId) === String(supplierId))
    : null;
  const fileSource = submissionData || stageData;

  // Memoize available files so they don't recreate on every render
  const availableFiles = React.useMemo(() => {
    const files = [];
    if (fileSource?.artworkUrl) {
      files.push({ type: 'image', url: fileSource.artworkUrl, name: 'Image' });
    }
    if (fileSource?.artworkData) {
      files.push({ type: 'pdf', data: fileSource.artworkData, mimeType: fileSource.artworkMimeType, fileName: fileSource.artworkFileName || 'PDF Document', name: fileSource.artworkFileName || 'PDF Document' });
    }
    return files;
  }, [fileSource?.artworkUrl, fileSource?.artworkData, fileSource?.artworkFileName, fileSource?.artworkMimeType]);

  const selectedFile = availableFiles[selectedFileIndex] || null;

  // Handle pinch-to-zoom on touchpad and touch screen
  const handleWheel = useCallback(e => {
    if (e.ctrlKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -20 : 20;
      setZoom(prevZoom => Math.max(50, Math.min(500, prevZoom + delta)));
    }
  }, []);

  useEffect(() => {
    const container = imageContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel, { passive: false });
    }
  }, [handleWheel]);

  const handleTouchStart = useCallback(e => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      e.currentTarget.dataset.initialDistance = distance;
      e.currentTarget.dataset.initialZoom = zoom;
    }
  }, [zoom]);

  const handleTouchMove = useCallback(e => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      const initialDistance = parseFloat(e.currentTarget.dataset.initialDistance);
      const initialZoom = parseFloat(e.currentTarget.dataset.initialZoom);

      if (initialDistance > 0) {
        const scale = distance / initialDistance;
        const newZoom = Math.round(Math.max(50, Math.min(500, initialZoom * scale)));
        setZoom(newZoom);
      }
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Load PDF if selected file is a PDF
  useEffect(() => {
    const selectedFile = availableFiles[selectedFileIndex];
    const isPdfUrl = selectedFile?.type === 'pdf' || (selectedFile?.type === 'image' && selectedFile?.url?.match(/\.pdf$/i));
    const hasPdfData = selectedFile?.type === 'pdf' && selectedFile?.data;

    if (isPdfUrl || hasPdfData) {
      setPdfLoading(true);

      const loadPdf = async () => {
        try {
          let arrayBuffer;

          if (hasPdfData && selectedFile.data) {
            // PDF stored in database - convert base64 to ArrayBuffer
            try {
              const binaryString = atob(selectedFile.data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              arrayBuffer = bytes.buffer;
            } catch (decodeError) {
              console.error('Error decoding base64 PDF data:', decodeError);
              setPdfLoading(false);
              setPdfDoc(null);
              toast.error('Could not decode PDF file data');
              return;
            }
          } else {
            // PDF from Cloudinary URL
            const response = await fetch(selectedFile.url);
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }
            arrayBuffer = await response.arrayBuffer();
          }

          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          setPdfDoc(pdf);
          setTotalPages(pdf.numPages);
          setCurrentPage(1);
          setPdfLoading(false);
        } catch (error) {
          console.error('Error loading PDF:', error);
          setPdfLoading(false);
          setPdfDoc(null);
          toast.error('Could not preview PDF - download to view');
        }
      };

      loadPdf();
    } else {
      setPdfDoc(null);
      setCurrentPage(1);
      setTotalPages(0);
    }
  }, [selectedFileIndex, availableFiles]);

  // Render PDF page
  useEffect(() => {
    let isMounted = true;

    if (pdfDoc && canvasRef.current) {
      // Cancel any pending render task before starting a new one
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      pdfDoc.getPage(currentPage).then(page => {
        if (!isMounted) return; // Component unmounted

        const canvas = canvasRef.current;
        if (!canvas) return; // Canvas might be unmounted

        const ctx = canvas.getContext('2d');
        const scale = (zoom / 100) * 1.5;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderTask = page.render({
          canvasContext: ctx,
          viewport: viewport
        });

        // Store render task so we can cancel it if needed
        renderTaskRef.current = renderTask;

        renderTask.promise
          .then(() => {
            if (isMounted) {
              renderTaskRef.current = null;
            }
          })
          .catch(error => {
            // Ignore cancellation errors
            if (error?.message?.includes('Rendering cancelled')) {
              return;
            }
            if (isMounted) {
              console.error('Error rendering PDF page:', error);
              renderTaskRef.current = null;
            }
          });
      }).catch(error => {
        if (isMounted) {
          console.error('Error getting PDF page:', error);
        }
      });
    }

    // Cleanup: set unmounted flag and cancel ongoing render
    return () => {
      isMounted = false;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, zoom]);

  // Download handler for selected artwork (image or pdf)
  const downloadArtwork = async () => {
    if (!selectedFile) {
      toast.error('No artwork available to download');
      return;
    }

    try {
      if (selectedFile.type === 'pdf' || (selectedFile.url && selectedFile.url.match(/\.pdf$/i))) {
        // PDF: either base64 stored in selectedFile.data or a URL
        if (selectedFile.data) {
          const binaryString = atob(selectedFile.data);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
          const blob = new Blob([bytes.buffer], { type: selectedFile.mimeType || 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = selectedFile.fileName || 'artwork.pdf';
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } else {
          const resp = await fetch(selectedFile.url);
          if (!resp.ok) throw new Error('Failed to fetch file');
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          // derive filename from URL
          const urlName = selectedFile.fileName || selectedFile.url.split('/').pop().split('?')[0] || 'artwork.pdf';
          a.download = urlName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }
      } else {
        // Image
        const resp = await fetch(selectedFile.url);
        if (!resp.ok) throw new Error('Failed to fetch file');
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const ext = (blob.type && blob.type.split('/')[1]) || 'png';
        const fname = (selectedFile.fileName || selectedFile.name || 'artwork') + `.${ext}`;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download error', err);
      toast.error('Failed to download artwork');
    }
  };

  const nextPdfPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPdfPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Validate checklist: each item must be either ticked OR have a comment
  const isChecklistValid = () => {
    return checklist.every(row => {
      const isTicked = row.ticks?.[0];
      const hasComment = row.comments?.[0]?.trim();
      return isTicked || hasComment;
    });
  };

  // Get list of invalid items for user feedback
  const getInvalidItems = () => {
    return checklist
      .map((row, idx) => ({idx, item: row.item, isTicked: row.ticks?.[0], hasComment: row.comments?.[0]?.trim()}))
      .filter(({isTicked, hasComment}) => !isTicked && !hasComment)
      .map(({item}) => item);
  };

  const handleSubmit = async (statusValue) => {
    if (!isChecklistValid()) {
      toast.error(`Please complete all checklist items: ${getInvalidItems().join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { stage: stageNum, status: statusValue, checklist };
      if (supplierId) payload.supplierId = supplierId;
      await onSubmit(payload);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
      throw err;
    } finally {
      setIsSubmitting(false);
      setConfirmAction(null);
    }
  };

  const handleCheckAll = () => {
    setChecklist(checklist.map(row => ({ ...row, ticks: [true] })));
  };

  const handleConfirmAction = (status) => {
    handleSubmit(status);
  };

  return (
    <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'white', zIndex:1000, display:'flex', flexDirection:'column'}}>
      {/* Header */}
      <div style={{padding:'16px 24px', borderBottom:'1px solid var(--gray-200)', background:'var(--gray-50)', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div>
          <h2 style={{fontSize:'1.25rem', fontWeight:700, margin:0}}>Review &amp; approve</h2>
          <p style={{fontSize:'0.8125rem', color:'var(--gray-600)', margin:'4px 0 0 0'}}>
            Stage {stageNum}: {STAGES[stageNum]?.label} · {ROLES[user?.role]?.label} · {user?.name}
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={20} /></button>
      </div>

      {/* Main Content */}
      <div style={{flex:1, display:'flex', overflowY:'hidden', gap:0}}>
        {/* Left: Image Viewer */}
        <div style={{flex:'0 0 50%', borderRight:'1px solid var(--gray-200)', padding:'16px', display:'flex', flexDirection:'column', overflowY:'auto', background:'var(--gray-50)'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
            <div>
              <h3 style={{fontSize:'0.875rem', fontWeight:600, margin:'0 0 8px 0'}}>Artwork for review</h3>
              {availableFiles.length > 1 && (
                <div className="file-switcher">
                  {availableFiles.map((file, idx) => (
                    <button
                      key={idx}
                      className={`file-switcher-btn ${selectedFileIndex === idx ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedFileIndex(idx);
                        setCurrentPage(1); // Reset PDF page when switching files
                      }}
                    >
                      {file.type === 'pdf' ? <FileText size={13} /> : <Upload size={13} />} {file.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="zoom-controls">
              <button className="btn btn-sm btn-secondary" onClick={() => setZoom(Math.max(50, zoom - 20))} disabled={zoom <= 50}>
                <ZoomOut size={16} />
              </button>
              <span className="zoom-readout">{zoom}%</span>
              <button className="btn btn-sm btn-secondary" onClick={() => setZoom(Math.min(500, zoom + 20))} disabled={zoom >= 500}>
                <ZoomIn size={16} />
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => downloadArtwork()} title="Download artwork" style={{marginLeft:8}}>
                <ExternalLink size={14} /> Download
              </button>
            </div>
          </div>

          {selectedFile ? (
            <div
              ref={imageContainerRef}
              style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', border:'1px solid var(--gray-200)', borderRadius:'var(--radius-md)', background:'white', overflow:'auto', touchAction:'none'}}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
            >
              {(selectedFile.type === 'pdf' || selectedFile.url?.match(/\.pdf$/i)) ? (
                <>
                  {pdfLoading ? (
                    <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%'}}>
                      <p style={{color:'var(--gray-500)'}}>Loading PDF…</p>
                    </div>
                  ) : !pdfDoc ? (
                    <div className="viewer-empty-state">
                      <FileText size={48} strokeWidth={1.5} />
                      <p className="title">Unable to preview PDF</p>
                      <p className="subtitle">Use the download button below</p>
                    </div>
                  ) : (
                    <>
                      <canvas ref={canvasRef} style={{maxWidth:'100%', maxHeight:'100%', display:'block'}} />
                      {totalPages > 1 && (
                        <div className="pdf-pagination">
                          <button className="btn btn-sm btn-secondary" onClick={prevPdfPage} disabled={currentPage <= 1}>
                            <ChevronLeft size={14} /> Previous
                          </button>
                          <span>Page {currentPage} of {totalPages}</span>
                          <button className="btn btn-sm btn-secondary" onClick={nextPdfPage} disabled={currentPage >= totalPages}>
                            Next <ChevronRight size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <img src={selectedFile.url} alt="Artwork for review" style={{maxWidth:'100%', maxHeight:'100%', width:'auto', height:'auto', transform:`scale(${zoom/100})`, userSelect:'none'}} />
              )}
            </div>
          ) : (
            <div style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid var(--gray-200)', borderRadius:'var(--radius-md)', background:'white', color:'var(--gray-400)'}}>
              <p>No artwork image available</p>
            </div>
          )}
          <p className="zoom-tip">
            <Info size={13} /> Tip: scroll with Ctrl held (or pinch on touchpad/mobile) to zoom
          </p>
        </div>

        {/* Right: Checklist & Controls */}
        <div style={{flex:'0 0 50%', padding:'16px', display:'flex', flexDirection:'column', overflowY:'auto'}}>
          <div className="info-banner tone-info" style={{marginBottom:16}}>
            <Info size={16} />
            <p className="info-banner-body" style={{color:'var(--gray-700)'}}>
              <strong>Note:</strong> each checklist item must be either ticked or have a comment before you can submit.
            </p>
          </div>

          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
            <p style={{fontWeight:600, fontSize:'0.875rem', margin:0}}>Checklist</p>
            <button 
              className="btn btn-sm" 
              onClick={handleCheckAll}
              style={{
                background: 'linear-gradient(135deg, #0033a0 0%, #001f6b 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.8125rem',
                padding: '6px 14px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 51, 160, 0.3)',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.boxShadow = '0 4px 12px rgba(0, 51, 160, 0.45)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.boxShadow = '0 2px 8px rgba(0, 51, 160, 0.3)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              ✓ Check All
            </button>
          </div>
          <div style={{flex:1, overflowY:'auto', marginBottom:20}}>
            <ChecklistTable checklist={checklist} onChange={setChecklist} readOnly={false} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{padding:'16px 24px', borderTop:'1px solid var(--gray-200)', background:'var(--gray-50)', display:'flex', justifyContent:'flex-end', gap:12}}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-danger"
          onClick={() => setConfirmAction('changes_requested')}
          disabled={!isChecklistValid()}
          title={!isChecklistValid() ? 'Complete all checklist items first' : ''}>
          <XCircle size={15}/> Reject
        </button>
        <button
          className="btn btn-success"
          onClick={() => setConfirmAction('approved')}
          disabled={!isChecklistValid()}
          title={!isChecklistValid() ? 'Complete all checklist items first' : ''}>
          <CheckCircle size={15}/> Approve
        </button>
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000}}>
          <div style={{background:'white', borderRadius:'var(--radius-lg)', padding:'24px', maxWidth:'400px', width:'90%', boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>
            <div className={`confirm-dialog-icon ${confirmAction === 'approved' ? 'tone-success' : 'tone-danger'}`}>
              {confirmAction === 'approved' ? <CheckCircle size={22} /> : <AlertTriangle size={22} />}
            </div>
            <h2 className="confirm-dialog-title">
              {confirmAction === 'approved' ? 'Approve artwork?' : 'Reject artwork?'}
            </h2>
            <p style={{fontSize:'0.875rem', color:'var(--gray-600)', margin:'0 0 20px 0', lineHeight:1.5}}>
              {confirmAction === 'approved'
                ? 'Are you sure you want to approve this artwork? Head approval is required to advance the stage.'
                : 'Are you sure you want to reject this artwork? The admin will need to address your feedback and resubmit.'}
            </p>
            <div style={{display:'flex', gap:12, justifyContent:'flex-end'}}>
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmAction(null)}
                disabled={isSubmitting}>
                Cancel
              </button>
              <button
                className={`btn ${confirmAction === 'approved' ? 'btn-success' : 'btn-danger'}`}
                onClick={() => handleConfirmAction(confirmAction)}
                disabled={isSubmitting}>
                {isSubmitting ? <span className="spinner" /> : confirmAction === 'approved' ? <><CheckCircle size={15} /> Approve</> : <><XCircle size={15} /> Reject</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReuploadPanel({ artworkId, stageNum, isSupplier, stageData, onSuccess }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [removePreviousArtwork, setRemovePreviousArtwork] = useState(false);

  const existingStageArtwork = !!(stageData?.artworkUrl || stageData?.artworkData);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: {'image/*':[], 'application/pdf':[]}, maxSize: 20*1024*1024, multiple:true
  });

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      let uploadedImage = null;
      let uploadedDocument = null;

      // Upload each file
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const uploadRes = await api.post('/upload', fd);

        if (uploadRes.data.isImage) {
          uploadedImage = {
            artworkUrl: uploadRes.data.url,
            artworkPublicId: uploadRes.data.publicId
          };
        } else {
          uploadedDocument = {
            artworkUrl: uploadRes.data.url || null,
            artworkPublicId: uploadRes.data.publicId || null,
            artworkFileName: uploadRes.data.fileName,
            artworkMimeType: uploadRes.data.mimeType,
            artworkData: uploadRes.data.data || null
          };
        }
      }

      // Prepare the update payload
      const uploadData = { stage: stageNum };
      if (uploadedImage) Object.assign(uploadData, uploadedImage);
      if (uploadedDocument) Object.assign(uploadData, uploadedDocument);
      if (!isSupplier && existingStageArtwork) {
        uploadData.removePreviousArtwork = removePreviousArtwork;
      }

      const endpoint = isSupplier ? `/artworks/${artworkId}/supplier-upload` : `/artworks/${artworkId}/stage-artwork`;
      await api.patch(endpoint, uploadData);

      toast.success('Artwork uploaded successfully!');
      setFiles([]);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{marginTop:16}}>
      {existingStageArtwork && (
        <div className="info-banner tone-warning" style={{marginBottom:16, flexDirection:'column'}}>
          <div style={{display:'flex', gap:10, alignItems:'flex-start'}}>
            <AlertTriangle size={18} color="var(--warning)" style={{marginTop:1}} />
            <div>
              <p className="info-banner-title">Existing artwork detected</p>
              <p className="info-banner-body">
                Do you want to remove the previously uploaded artwork before uploading the new file, or keep it as an archive?
              </p>
            </div>
          </div>
          <div style={{display:'flex', gap:10, flexWrap:'wrap', marginTop:12}}>
            <button
              type="button"
              className={`btn btn-sm ${removePreviousArtwork ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setRemovePreviousArtwork(true)}
            >
              Remove previous artwork
            </button>
            <button
              type="button"
              className={`btn btn-sm ${!removePreviousArtwork ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setRemovePreviousArtwork(false)}
            >
              Keep previous artwork
            </button>
          </div>
          {removePreviousArtwork && (
            <p className="info-banner-body" style={{marginTop:10}}>
              The visible artwork will not disappear immediately. It will be removed after the new upload succeeds and the detail view refreshes.
            </p>
          )}
        </div>
      )}
      {files.length === 0 ? (
        <div {...getRootProps()} className={`dropzone ${isDragActive ? 'is-active' : ''}`}>
          <input {...getInputProps()} />
          <UploadCloud size={24} />
          <p className="dropzone-title">
            {isDragActive ? 'Drop files here' : 'Drag & drop or click to upload'}
          </p>
          <p className="dropzone-sub">
            Accepted formats: PNG, JPG, PDF (max 20MB each)
          </p>
        </div>
      ) : (
        <>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:12, marginBottom:16}}>
            {files.map((file, idx) => (
              <div key={idx} className="upload-file-card">
                <div className="upload-file-card-header">
                  <div>
                    <p className="upload-file-card-name">{file.name}</p>
                    <p className="upload-file-card-size">{(file.size/1024/1024).toFixed(2)} MB</p>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => removeFile(idx)}
                    style={{padding:'4px 8px', minWidth:'auto'}}
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="upload-file-card-preview">
                  {file.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(file)} alt="preview" />
                  ) : (
                    <div className="doc-icon">
                      <FileText size={32} strokeWidth={1.5} />
                      <span>PDF</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{display:'flex', gap:8}}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleUpload}
              disabled={uploading}
              style={{flex:1}}
            >
              {uploading ? <span className="spinner" /> : <><Upload size={14}/> Upload all</>}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setFiles([])}
              disabled={uploading}
              style={{flex:1}}
            >
              Clear all
            </button>
          </div>

          <div {...getRootProps()} className="dropzone-secondary">
            <input {...getInputProps()} />
            <p>Add more files</p>
          </div>
        </>
      )}
    </div>
  );
}

export default function ArtworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const userId = user?._id || user?.id;
  const userEmail = user?.email || '';
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approvalModal, setApprovalModal] = useState(null); // stageNum
  const [viewChecklistModal, setViewChecklistModal] = useState(null); // { approval, role, stageNum }
  const [continueReviewModal, setContinueReviewModal] = useState(null); // { stage, supplierId, supplierName }
  const [finalizeModal, setFinalizeModal] = useState(null); // { supplierId, isEdit, existingFileName }
  const [reopenModal, setReopenModal] = useState(null); // { stageNum, supplierId, supplierName }
  const [revisionHistoryModal, setRevisionHistoryModal] = useState(null); // { supplierId, supplierName }
  const [expandedStages, setExpandedStages] = useState({1:true, 2:false, 3:false, 4:false});
  const [editingSharePointLink, setEditingSharePointLink] = useState(false);
  const [sharePointLinkInput, setSharePointLinkInput] = useState('');
  const [reminderSubmittingStage, setReminderSubmittingStage] = useState(null);
  const [reminderSubmittingApproval, setReminderSubmittingApproval] = useState(null);
  const reopenFromNavigation = useRef(location.state?.reopenSubmission || null);
  const returnTab = location.state?.activeTab || getStoredArtworkListTab(DEFAULT_ARTWORK_LIST_TAB);
  const returnPage = Math.max(1, Number(location.state?.currentPage) || 1);

  const fetchArtwork = useCallback(async () => {
    try {
      const res = await api.get(`/artworks/${id}`);
      setArtwork(res.data);
      // Auto-expand current stage and collapse completed stages
      const newExpandedState = {1:false, 2:false, 3:false, 4:false};
      newExpandedState[res.data.currentStage] = true;
      // Collapse any completed stages
      for (let i = 1; i <= 4; i++) {
        if (res.data[`stage${i}`]?.status === 'completed') {
          newExpandedState[i] = false;
        }
      }
      const reopenTarget = reopenFromNavigation.current;
      if (reopenTarget && [3, 4].includes(Number(reopenTarget.stageNum))) {
        const stageNum = Number(reopenTarget.stageNum);
        newExpandedState[stageNum] = true;
        setReopenModal({ ...reopenTarget, stageNum });
        reopenFromNavigation.current = null;
      }
      setExpandedStages(newExpandedState);
      return res.data;
    } catch {
      toast.error('Failed to load artwork');
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchArtwork(); }, [fetchArtwork]);

  const getSupplierSubmissionKey = (supplierId) => {
    if (supplierId === undefined || supplierId === null) return null;
    if (typeof supplierId === 'object') {
      const idValue = supplierId._id || supplierId.id || supplierId;
      return idValue ? String(idValue) : null;
    }
    return String(supplierId);
  };

  const findNextSupplierSubmissionToReview = (artworkData, stageNum, currentSupplierId, role, userId, userEmail) => {
    const stageData = artworkData?.[`stage${stageNum}`];
    if (!stageData || stageData.status !== 'in_progress') return null;

    const submissions = Array.isArray(stageData?.submissions) ? stageData.submissions : [];
    const currentKey = getSupplierSubmissionKey(currentSupplierId);

    const assignedSuppliersRaw = artworkData?.supplierAssignmentInfo?.assignedSuppliers || artworkData?.suppliers || [];
    const assignedSupplierKeys = (Array.isArray(assignedSuppliersRaw) ? assignedSuppliersRaw : [])
      .map((supplier) => getSupplierSubmissionKey((supplier && supplier._id) || supplier))
      .filter(Boolean);

    const submissionMap = submissions.reduce((map, submission) => {
      const submissionKey = getSupplierSubmissionKey((submission.supplierId && submission.supplierId._id) || submission.supplierId);
      if (!submissionKey) return map;
      if (!map.has(submissionKey)) map.set(submissionKey, submission);
      return map;
    }, new Map());

    const nextSubmissionFromAssigned = assignedSupplierKeys
      .filter((key) => key && key !== currentKey)
      .map((key) => submissionMap.get(key))
      .find((submission) => submission && canUserApprove(artworkData, stageNum, role, userId, userEmail, (submission.supplierId && submission.supplierId._id) || submission.supplierId));

    const nextSubmission = nextSubmissionFromAssigned || submissions.find((submission) => {
      const submissionKey = getSupplierSubmissionKey((submission.supplierId && submission.supplierId._id) || submission.supplierId);
      return submissionKey && submissionKey !== currentKey && canUserApprove(artworkData, stageNum, role, userId, userEmail, (submission.supplierId && submission.supplierId._id) || submission.supplierId);
    });

    if (!nextSubmission) return null;

    const supplierNameMap = assignedSuppliersRaw.reduce((map, supplier) => {
      const id = supplier?._id ? String(supplier._id) : String(supplier);
      if (id) {
        map[id] = supplier?.name || supplier?.email || id;
      }
      return map;
    }, {});

    let nextSupplierName = 'Supplier';
    const submissionId = getSupplierSubmissionKey((nextSubmission.supplierId && nextSubmission.supplierId._id) || nextSubmission.supplierId);
    if (submissionId && supplierNameMap[submissionId]) {
      nextSupplierName = supplierNameMap[submissionId];
    } else if (nextSubmission.supplierId) {
      if (typeof nextSubmission.supplierId === 'object') {
        nextSupplierName = nextSubmission.supplierId.name || nextSubmission.supplierId.email || String(nextSubmission.supplierId._id || 'Supplier');
      } else {
        nextSupplierName = String(nextSubmission.supplierId);
      }
    }

    return {
      supplierId: submissionId,
      supplierName: nextSupplierName,
    };
  };

  const handleApprovalSubmit = async (payload) => {
    try {
      await api.post(`/artworks/${id}/approve`, payload);
      toast.success(payload.status === 'approved' ? 'Approved successfully!' : 'Changes requested!');
      const updatedArtwork = await fetchArtwork();

      if (payload.status === 'approved' && (payload.stage === 3 || payload.stage === 4) && payload.supplierId && updatedArtwork) {
        const currentKey = getSupplierSubmissionKey(payload.supplierId);
        const next = findNextSupplierSubmissionToReview(updatedArtwork, payload.stage, payload.supplierId, user?.role, userId, userEmail);
        if (next && next.supplierId && next.supplierId !== currentKey) {
          setContinueReviewModal({
            stage: payload.stage,
            supplierId: next.supplierId,
            supplierName: next.supplierName,
          });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
      throw err;
    }
  };

  const handleSaveSharePointLink = async () => {
    if (!sharePointLinkInput.trim()) {
      toast.error('Please enter a valid SharePoint link');
      return;
    }

    try {
      await api.patch(`/artworks/${id}`, { sharePointLink: sharePointLinkInput });
      toast.success('SharePoint link updated successfully!');
      setEditingSharePointLink(false);
      fetchArtwork();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update SharePoint link');
    }
  };

  const downloadApprovalSummary = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const addText = (text, options = {}) => {
      const { size = 11, style = 'normal', align = 'left', lineHeight = 1.4, indent = 0 } = options;
      doc.setFontSize(size);
      doc.setFont('helvetica', style);
      doc.setTextColor(33, 37, 41);
      const maxWidth = contentWidth - indent;
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, margin + indent, y, { align });
      y += lines.length * size * lineHeight;
    };

    const addKeyValue = (label, value) => {
      if (!value) return;
      ensurePage(24);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      const labelText = `${label}: `;
      const labelWidth = doc.getTextWidth(labelText);
      doc.text(labelText, margin, y);
      doc.setFont('helvetica', 'normal');
      const wrapped = doc.splitTextToSize(String(value), contentWidth - labelWidth);
      doc.text(wrapped, margin + labelWidth, y);
      y += wrapped.length * 14;
    };

    const addDivider = () => {
      y += 10;
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.8);
      doc.line(margin, y, pageWidth - margin, y);
      y += 16;
    };

    const ensurePage = (needed = 0) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addSectionHeader = (title, subtitle) => {
      ensurePage(42);
      doc.setFillColor(247, 250, 252);
      doc.roundedRect(margin, y, contentWidth, 42, 8, 8, 'F');
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(title, margin + 14, y + 26);
      if (subtitle) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        doc.text(subtitle, margin + contentWidth - 14, y + 26, { align: 'right' });
      }
      y += 58;
    };

    const addSummaryCards = (items) => {
      ensurePage(90);
      const cardWidth = (contentWidth - 18) / 2;
      const cardHeight = 64;
      items.forEach((item, index) => {
        const x = margin + (index % 2) * (cardWidth + 18);
        const top = y + Math.floor(index / 2) * (cardHeight + 12);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, top, cardWidth, cardHeight, 8, 8, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(x, top, cardWidth, cardHeight, 8, 8);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 65, 85);
        doc.text(item.title, x + 12, top + 22);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(17, 24, 39);
        doc.text(String(item.value), x + 12, top + 44);
      });
      y += 90;
    };

    const addStageBlock = (title, status, completedAt) => {
      ensurePage(70);
      doc.setFillColor(236, 246, 255);
      doc.roundedRect(margin, y, contentWidth, 34, 8, 8, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(title, margin + 12, y + 22);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(37, 99, 235);
      const statusText = `Status: ${status || 'Not started'}`;
      doc.text(statusText, margin + contentWidth - 12, y + 22, { align: 'right' });
      y += 50;
      if (completedAt) addKeyValue('Completed', formatDateTime(completedAt));
    };

    const addBulletList = (title, items, indent = 12) => {
      if (!items || items.length === 0) return;
      ensurePage(20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      doc.text(title, margin, y);
      y += 18;
      items.forEach((item) => {
        ensurePage(24);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);
        const bullet = '• ';
        const wrapped = doc.splitTextToSize(item, contentWidth - indent - 6);
        doc.text(bullet, margin + indent - 6, y);
        doc.text(wrapped, margin + indent + 6, y);
        y += wrapped.length * 14;
      });
      y += 8;
    };

    const addPageNumbers = () => {
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
      }
    };

    addSectionHeader('Artwork Approval Summary', `Generated on ${formatDateTime(new Date())}`);
    addText(artwork.title || 'Untitled artwork', { size: 16, style: 'bold', lineHeight: 1.2 });
    y += 4;

    addSummaryCards([
      { title: 'SKU', value: artwork.sku || 'N/A' },
      { title: 'Brand', value: artwork.brand || 'N/A' },
      { title: 'Overall status', value: artwork.overallStatus || 'N/A' },
      { title: 'Completed', value: artwork.overallCompletedAt ? formatDateTime(artwork.overallCompletedAt) : 'Pending' },
    ]);

    if (artwork.sharePointLink) {
      addKeyValue('SharePoint link', artwork.sharePointLink);
      addDivider();
    }

    const stageLabelText = (stage) => STAGE_LABELS[stage]?.title || `Stage ${stage}`;
    const stageDescText = (stage) => STAGE_LABELS[stage]?.desc || '';

    for (let stageNum = 1; stageNum <= 4; stageNum++) {
      const stageData = artwork[`stage${stageNum}`] || {};
      addStageBlock(`Stage ${stageNum}: ${stageLabelText(stageNum)}`, stageData.status, stageData.completedAt);
      if (stageDescText(stageNum)) addText(stageDescText(stageNum), { size: 11, style: 'normal', lineHeight: 1.4 });

      if (stageData.artworkUrl || stageData.artworkFileName) {
        addBulletList('Artwork file', [
          stageData.artworkFileName ? `File: ${stageData.artworkFileName}` : null,
          stageData.artworkUrl ? `URL: ${stageData.artworkUrl}` : null,
        ].filter(Boolean), 18);
      }

      const approvalsToReport = (stageNum >= 3 && stageData.submissions?.length > 0)
        ? []
        : (stageData.approvals || []);

      if (stageNum >= 3 && stageData.submissions?.length > 0) {
        const supplierNameMap = (artwork.supplierAssignmentInfo?.assignedSuppliers || []).reduce((map, supplier) => {
          if (!supplier) return map;
          const supplierId = String(supplier._id || supplier);
          map[supplierId] = supplier.name || supplier.email || 'Supplier';
          return map;
        }, {});

        stageData.submissions.forEach((submission, index) => {
          ensurePage(90);
          addSectionHeader(`Supplier ${index + 1}`, supplierNameMap[String((submission.supplierId && submission.supplierId._id) || submission.supplierId || `missing-${index}`)] || `Supplier ${index + 1}`);
          if (submission.artworkFileName) addKeyValue('File', submission.artworkFileName);
          if (submission.artworkUrl) addKeyValue('URL', submission.artworkUrl);

          const submissionApprovals = submission.approvals || [];
          if (submissionApprovals.length > 0) {
            submissionApprovals.forEach((approval) => {
              const reviewer = approval.assignedUserName || approval.userName || approval.assignedUserEmail || 'Pending assignment';
              addBulletList('Approval', [
                `${ROLES[approval.role]?.label || approval.role}: ${approval.status || 'pending'} (${reviewer})`,
                approval.comments ? `Comment: ${approval.comments}` : null,
                approval.signedAt ? `Signed at: ${formatDateTime(approval.signedAt)}` : null,
              ].filter(Boolean), 24);
            });
          }

          if (stageNum === 4 && submission.adminApproval && submission.adminApproval.uploadedAt) {
            addKeyValue('Admin final sign-off', '');
            addBulletList('Details', [
              `Uploaded by: ${submission.adminApproval.uploaderName || 'Admin'}`,
              `Uploaded at: ${formatDateTime(submission.adminApproval.uploadedAt)}`,
              submission.adminApproval.artworkFileName ? `File: ${submission.adminApproval.artworkFileName}` : null,
              submission.adminApproval.artworkUrl ? `URL: ${submission.adminApproval.artworkUrl}` : null,
              submission.adminApproval.comment ? `Comment: ${submission.adminApproval.comment}` : null,
            ].filter(Boolean), 24);
          }
        });
      } else if (approvalsToReport.length > 0) {
        approvalsToReport.forEach((approval) => {
          const reviewer = approval.assignedUserName || approval.userName || approval.assignedUserEmail || 'Pending assignment';
          addBulletList('Approval', [
            `${ROLES[approval.role]?.label || approval.role}: ${approval.status || 'pending'} (${reviewer})`,
            approval.comments ? `Comment: ${approval.comments}` : null,
            approval.signedAt ? `Signed at: ${formatDateTime(approval.signedAt)}` : null,
          ].filter(Boolean), 24);
        });
      }

      addDivider();
    }

    addPageNumbers();
    const fileNameSafe = artwork.title ? artwork.title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_') : 'approval_summary';
    doc.save(`${fileNameSafe}_approval_summary.pdf`);
  };

  const handleSendReminder = async (stageNum, supplierIdOverride) => {
    setReminderSubmittingStage(stageNum);
    const supplierId = (stageNum === 3 || stageNum === 4) ? supplierIdOverride : undefined;

    if ((stageNum === 3 || stageNum === 4) && !supplierId) {
      toast.error('Select a supplier submission before sending a Stage 3/4 reminder');
      setReminderSubmittingStage(null);
      return;
    }

    try {
      await api.post(`/artworks/${id}/marketing-reminder`, buildReminderRequest({ stage: stageNum, supplierId }));
      toast.success('Reminder sent successfully');
      fetchArtwork();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminder');
    } finally {
      setReminderSubmittingStage(null);
    }
  };

  const handleSendReminderToApproval = async (stageNum, approval, supplierIdOverride) => {
    if (!approval) return;
    const key = `${stageNum}-${approval.role}-${approval.assignedUserId || approval.assignedUserEmail || 'unknown'}`;
    const supplierId = (stageNum === 3 || stageNum === 4) ? supplierIdOverride : undefined;

    if ((stageNum === 3 || stageNum === 4) && !supplierId) {
      toast.error('Supplier submission is missing for this reminder');
      return;
    }

    setReminderSubmittingApproval(key);
    try {
      await api.post(`/artworks/${id}/marketing-reminder`, buildReminderRequest({ stage: stageNum, approval, supplierId }));
      toast.success(`Reminder sent to ${approval.assignedUserName || approval.userName || approval.assignedUserEmail || 'the reviewer'}`);
      fetchArtwork();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminder');
    } finally {
      setReminderSubmittingApproval(null);
    }
  };

  // Decode base64 (including data-URI or url-safe variants) into a Blob
  const decodeBase64ToBlob = (base64Data, mimeType = 'application/pdf') => {
    if (!base64Data) throw new Error('No data');
    let b64 = String(base64Data || '');
    const comma = b64.indexOf(',');
    if (comma !== -1) b64 = b64.slice(comma + 1);
    // Handle URL-safe base64
    b64 = b64.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const binaryString = atob(b64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
    return new Blob([bytes.buffer], { type: mimeType || 'application/pdf' });
  };

  if (loading) return (
    <div className="page-layout"><Sidebar />
      <div className="main-content" style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
        <div className="spinner spinner-dark" style={{width:36,height:36}} />
      </div>
    </div>
  );

  if (!artwork) return null;

  return (
    <div className="page-layout">
      <Sidebar />
      <div className="main-content">
        <Header title={artwork.title} />
        <div className="page-body">
          <button
            className="btn btn-ghost btn-sm"
            style={{marginBottom:20}}
            onClick={() => navigate('/artworks', { state: { activeTab: returnTab, currentPage: returnPage } })}
          >
            <ArrowLeft size={16} /> Back to artworks
          </button>

          {/* SharePoint Link Info - Only for Admin & Suppliers */}
          {user?.role === 'admin' ? (
            <div className="sharepoint-banner">
              <p className="sharepoint-banner-title"><Link2 size={16} /> SharePoint link</p>

              {!editingSharePointLink ? (
                <>
                  <p style={{fontSize:'0.8125rem', color:'var(--gray-700)', margin:'0 0 12px 0'}}>
                    {artwork.sharePointLink ? 'SharePoint folder for this artwork project.' : 'No SharePoint link set yet.'}
                  </p>
                  <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setSharePointLinkInput(artwork.sharePointLink || '');
                        setEditingSharePointLink(true);
                      }}
                      style={{display:'flex', alignItems:'center', gap:'6px', padding:'6px 12px', color:'var(--info)', border:'1px solid var(--info)', borderRadius:'4px'}}
                    >
                      <Edit size={14} /> Edit
                    </button>
                    {artwork.sharePointLink && (
                      <a href={artwork.sharePointLink} target="_blank" rel="noopener noreferrer" className="btn btn-info btn-sm" style={{whiteSpace:'nowrap'}}>
                        <ExternalLink size={14} /> Open SharePoint
                      </a>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <div className="sharepoint-link-form" style={{marginBottom:12}}>
                    <input
                      type="text"
                      value={sharePointLinkInput}
                      onChange={(e) => setSharePointLinkInput(e.target.value)}
                      placeholder="Paste SharePoint folder link here…"
                      style={{padding:'8px 12px', border:'1px solid var(--gray-300)', borderRadius:'var(--radius-sm)', fontSize:'0.8125rem'}}
                    />
                  </div>
                  <div style={{display:'flex', gap:8}}>
                    <button className="btn btn-primary btn-sm" onClick={handleSaveSharePointLink}>
                      <Save size={14} /> Save link
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingSharePointLink(false)}>
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : artwork.sharePointLink && (user?.role === 'supplier') ? (
            <div className="sharepoint-banner compact">
              <div>
                <p className="sharepoint-banner-title"><Link2 size={16} /> SharePoint link available</p>
                <p style={{fontSize:'0.8125rem', color:'var(--gray-700)', margin:0}}>
                  Access the artwork details and supporting files via SharePoint.
                </p>
              </div>
              <a href={artwork.sharePointLink} target="_blank" rel="noopener noreferrer" className="btn btn-info btn-sm" style={{whiteSpace:'nowrap'}}>
                <ExternalLink size={14} /> Open SharePoint
              </a>
            </div>
          ) : null}

          {/* Stage Progress */}
          <div className="card" style={{padding:'24px 32px', marginBottom:24}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24}}>
              <div>
                <h3 style={{fontSize:'0.9375rem', fontWeight:700}}>Approval progress</h3>
                <p className="overall-status-line">
                  {artwork.overallStatus === 'completed' ? (
                    <>
                      <CheckCircle size={14} style={{color:'var(--success-color)'}} />
                      All stages completed on {formatDateTime(artwork.overallCompletedAt)}
                    </>
                  ) : (
                    `Currently in Stage ${artwork.currentStage}: ${STAGES[artwork.currentStage]?.label}`
                  )}
                </p>
              </div>
              <div style={{display:'flex', alignItems:'center', gap: 12}}>
                {artwork.overallStatus === 'completed' && (
                  <button className="btn btn-primary btn-sm" onClick={downloadApprovalSummary} style={{whiteSpace:'nowrap'}}>
                    <FileText size={14} /> Download Approval Summary
                  </button>
                )}
                <span className={`badge ${artwork.overallStatus === 'completed' ? 'badge-success' : artwork.overallStatus === 'on_hold' ? 'badge-warning' : 'badge-info'} badge-with-icon`}
                  style={{fontSize:'0.8125rem', padding:'5px 14px'}}>
                  {artwork.overallStatus === 'completed' ? (
                    <>
                      <Trophy size={14} /> Fully approved
                    </>
                  ) : artwork.overallStatus === 'on_hold' ? 'On hold' : 'Active'}
                </span>
              </div>
            </div>
            <StageProgress currentStage={artwork.currentStage} artwork={artwork} />
          </div>

          {/* Stage Panels */}
          {[1,2,3,4].map(stageNum => {
            const stageData = artwork[`stage${stageNum}`];
            const stageLabel = STAGE_LABELS[stageNum];
            const isCurrentStage = artwork.currentStage === stageNum;
            const isExpanded = expandedStages[stageNum];
            const userId = user?._id || user?.id;
            const userEmail = user?.email || '';
            // Normalize assigned suppliers so we can handle legacy cases where the
            // assignedSuppliers array may contain plain IDs or populated objects.
            const assignedSuppliersRaw = artwork.supplierAssignmentInfo?.assignedSuppliers || [];
            const assignedSuppliers = assignedSuppliersRaw.map(s => {
              if (!s) return null;
              if (typeof s === 'string' || typeof s === 'number') return { _id: String(s), name: null, email: null };
              // If Mongoose populated object, ensure _id and name/email exist
              return { _id: String(s._id || s), name: s.name || null, email: s.email || null };
            }).filter(Boolean);

            const assignedSupplierIds = assignedSuppliers.map(s => String(s._id));
            const submissionSupplierIds = (stageData?.submissions || []).map(s => String((s.supplierId && s.supplierId._id) || s.supplierId));
            const missingSupplierIds = assignedSupplierIds.filter(id => !submissionSupplierIds.includes(id));
            const hasAllSupplierSubmissions = assignedSupplierIds.length === 0 || missingSupplierIds.length === 0;

            const canApprove = user?.role !== 'admin' && isCurrentStage && canUserApprove(artwork, stageNum, user?.role, userId, userEmail) && !((stageNum === 3 || stageNum === 4) && !hasAllSupplierSubmissions);
            const myApproval = stageData?.approvals?.find(a => {
              if (a.role !== user?.role) return false;
              const approvalUserId = a.assignedUserId ? String(a.assignedUserId) : '';
              const approvalEmail = (a.assignedUserEmail || '').toLowerCase().trim();
              const normalizedEmail = (userEmail || '').toLowerCase().trim();
              return (
                (!userId || !approvalUserId || approvalUserId === String(userId) || approvalUserId === String(userId?._id || '')) ||
                (normalizedEmail && approvalEmail && approvalEmail === normalizedEmail) ||
                a.status === 'pending'
              );
            });
            const alreadyApproved = myApproval?.status === 'approved';
            const isChanges = stageData?.status === 'changes_requested';
            const pendingReviewers = getPendingReviewersForStage(stageData);
            const pendingReminderApprovals = (stageData?.approvals || []).filter(a => a.status === 'pending');

            const supplierNameMap = assignedSuppliers.reduce((map, supplier) => {
              const id = String(supplier._id || supplier);
              map[id] = supplier.name || supplier.email || 'Supplier';
              return map;
            }, {});

            const supplierSubmissions = stageData?.submissions?.slice() || [];
            const rootArtworkUrl = stageData?.artworkUrl || null;
            const rootArtworkData = stageData?.artworkData || null;
            const rootArtworkFileName = stageData?.artworkFileName || null;
            const rootArtworkMimeType = stageData?.artworkMimeType || null;

            if (stageNum >= 3 && !stageData?.submissions?.length && (rootArtworkUrl || rootArtworkData || rootArtworkFileName || rootArtworkMimeType)) {
              const assignedSupplierIds = assignedSuppliers.map((supplier) => String(supplier._id));
              const existingSubmissionSupplierIds = supplierSubmissions.map((submission) => String(submission.supplierId));
              const missingSupplierIds = assignedSupplierIds.filter((supplierId) => !existingSubmissionSupplierIds.includes(supplierId));

              // For any assigned supplier that doesn't have an explicit submission,
              // create a synthetic submission that points at the root artwork so each
              // supplier block can show the supplier name consistently. This covers
              // cases with multiple assigned suppliers where only the root artwork exists.
              if (missingSupplierIds.length > 0) {
                missingSupplierIds.forEach((missingId) => {
                  const syntheticSubmission = {
                    supplierId: missingId,
                    artworkUrl: rootArtworkUrl,
                    artworkPublicId: stageData.artworkPublicId || null,
                    artworkFileName: rootArtworkFileName,
                    artworkMimeType: rootArtworkMimeType,
                    artworkData: rootArtworkData,
                    approvals: stageData?.approvals ? JSON.parse(JSON.stringify(stageData.approvals)) : [],
                    uploadedAt: stageData.uploadedAt || null
                  };

                  // If the root artwork is actually a file upload (PDF) but the URL is an image,
                  // avoid rendering the duplicate image preview for the synthetic supplier submission.
                  if (rootArtworkData && rootArtworkMimeType && !rootArtworkUrl?.match(/\.pdf$/i) && /\.(jpg|jpeg|png|webp)$/i.test(rootArtworkUrl)) {
                    syntheticSubmission.artworkUrl = null;
                  }

                  supplierSubmissions.push(syntheticSubmission);
                });
              }
            }

            const showStageDataPreview = stageNum <= 2 || (supplierSubmissions.length === 0 && (rootArtworkUrl || rootArtworkData || rootArtworkFileName || rootArtworkMimeType));

            return (
              <React.Fragment key={stageNum}>
                {/* Supplier Trigger Bar - between Stage 2 and Stage 3 */}
                {stageNum === 3 && isAdmin && artwork.stage2?.status === 'completed' && (
                  <SupplierTriggerBar artwork={artwork} onAssign={fetchArtwork} />
                )}

                <div className={`card stage-panel ${isCurrentStage ? 'active-stage' : ''} ${stageData?.status === 'completed' ? 'completed-stage' : ''}`} style={{marginBottom:16}}>
                {/* Stage header */}
                <div className="stage-panel-header" onClick={() => setExpandedStages(p => ({
                  1: p[stageNum] === true ? false : stageNum === 1,
                  2: p[stageNum] === true ? false : stageNum === 2,
                  3: p[stageNum] === true ? false : stageNum === 3,
                  4: p[stageNum] === true ? false : stageNum === 4,
                }))}>
                  <div style={{display:'flex', alignItems:'center', gap:12}}>
                    <div className={`stage-num-badge stage-${stageNum}-badge`}>{stageNum}</div>
                    <div>
                      <p style={{fontWeight:700, fontSize:'0.9375rem'}}>{stageLabel.title}</p>
                      <p style={{fontSize:'0.75rem', color:'var(--gray-500)', marginTop:1}}>{stageLabel.desc}</p>
                    </div>
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:10}}>
                    <span className={`badge badge-with-icon ${
                      stageData?.status === 'completed' ? 'badge-success' :
                      stageData?.status === 'in_progress' ? 'badge-info' :
                      stageData?.status === 'changes_requested' ? 'badge-danger' :
                      'badge-gray'
                    }`}>
                      {stageData?.status === 'completed' ? <><CheckCircle size={13} /> Completed</> :
                       stageData?.status === 'in_progress' ? 'In progress' :
                       stageData?.status === 'changes_requested' ? <><AlertTriangle size={13} /> Changes needed</> :
                       'Not started'}
                    </span>
                    {isExpanded ? <ChevronUp size={18} color="var(--gray-400)" /> : <ChevronDown size={18} color="var(--gray-400)" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="stage-panel-body">
                    {/* Artwork Image - Only visible to admin */}
                    {((((stageData?.submissions?.length > 0) || stageData?.artworkUrl || stageData?.artworkData) && user?.role === 'admin') || supplierSubmissions.length > 0) && (
                      <div style={{marginBottom:20}}>
                        <p style={{fontWeight:600, fontSize:'0.875rem', marginBottom:10}}>
                          {stageNum === 3 ? 'Position proof' : stageNum === 4 ? 'Colour proof scan' : 'Artwork file'}
                        </p>
                        <div style={{display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap'}}>
                          {/* Image Preview */}
                          {showStageDataPreview && stageData.artworkUrl && stageData.artworkUrl.match(/\.(jpg|jpeg|png|webp)$/i) && (
                            <div className="file-preview-card">
                              <div className="file-preview-thumb">
                                <img src={stageData.artworkUrl} alt="artwork" />
                              </div>
                              <a href={stageData.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                                <ExternalLink size={14} /> Open full size
                              </a>
                            </div>
                          )}

                          {/* PDF Preview */}
                          {showStageDataPreview && stageData.artworkData && (
                            <div className="file-preview-card">
                              <div className="file-preview-thumb is-doc">
                                <FileText size={36} strokeWidth={1.5} />
                                <p className="doc-title">PDF document</p>
                                <p className="doc-sub">Click to view</p>
                              </div>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => {
                                  try {
                                    const blob = decodeBase64ToBlob(stageData.artworkData, stageData.artworkMimeType || 'application/pdf');
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.target = '_blank';
                                    a.rel = 'noopener noreferrer';
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    setTimeout(() => URL.revokeObjectURL(url), 1500);
                                  } catch (error) {
                                    console.error('Error opening PDF:', error);
                                    toast.error('Could not open file: Invalid file data');
                                  }
                                }}
                              >
                                <ExternalLink size={14} /> Open full size
                              </button>
                            </div>
                          )}

                          {/* PDF from URL (legacy) */}
                          {showStageDataPreview && stageData.artworkUrl && stageData.artworkUrl.match(/\.pdf$/i) && !stageData.artworkData && (
                            <div className="file-preview-card">
                              <div className="file-preview-thumb is-doc">
                                <FileText size={36} strokeWidth={1.5} />
                                <p className="doc-title">PDF document</p>
                                <p className="doc-sub">Click to view</p>
                              </div>
                              <a href={stageData.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                                <ExternalLink size={14} /> Open full size
                              </a>
                            </div>
                          )}

                          {supplierSubmissions.length > 0 && supplierSubmissions.map((submission, index) => {
                            const supplierName = supplierNameMap[String(submission.supplierId)] || 'Supplier';
                            const submissionApprovals = submission.approvals || [];
                            const mySubmissionApproval = submissionApprovals.find(a => {
                              if (a.role !== user?.role) return false;
                              const approvalUserId = a.assignedUserId ? String(a.assignedUserId) : '';
                              const approvalEmail = (a.assignedUserEmail || '').toLowerCase().trim();
                              const normalizedEmail = (userEmail || '').toLowerCase().trim();
                              return (
                                (!userId || !approvalUserId || approvalUserId === String(userId) || approvalUserId === String(userId?._id || '')) ||
                                (normalizedEmail && approvalEmail && approvalEmail === normalizedEmail) ||
                                a.status === 'pending'
                              );
                            });
                            const submissionAlreadyApproved = mySubmissionApproval?.status === 'approved';
                            const submissionCanApprove = user?.role !== 'admin' &&
                              canUserApprove(artwork, stageNum, user?.role, userId, userEmail, submission.supplierId);
                            const palette = SUPPLIER_COLUMN_PALETTE[index % SUPPLIER_COLUMN_PALETTE.length];
                            return (
                              <div
                                key={`${submission.supplierId || index}-${index}`}
                                className="supplier-column"
                                style={{
                                  minWidth: 220,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 10,
                                  background: palette.bg,
                                  border: `1px solid ${palette.border}`,
                                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.65)',
                                }}
                              >
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap'}}>
                                  <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                                    <p style={{fontWeight:700, fontSize:'0.875rem', margin:0}}>{supplierName}</p>
                                    <span style={{
                                      fontSize:'0.65rem',
                                      fontWeight:700,
                                      color: submission.status === 'completed' ? 'var(--success-color)' : submission.status === 'changes_requested' ? 'var(--danger)' : 'var(--gray-700)',
                                      background: submission.status === 'completed' ? 'rgba(88,189,125,0.12)' : submission.status === 'changes_requested' ? 'rgba(255,128,128,0.12)' : 'rgba(255,255,255,0.85)',
                                      border: `1px solid ${submission.status === 'completed' ? 'rgba(88,189,125,0.4)' : submission.status === 'changes_requested' ? 'rgba(255,128,128,0.4)' : palette.border}`,
                                      borderRadius:'999px',
                                      padding:'4px 10px',
                                      whiteSpace:'nowrap',
                                    }}>
                                      {submission.status === 'completed' ? 'Completed' : submission.status === 'changes_requested' ? 'Changes requested' : 'In progress'}
                                    </span>
                                  </div>
                                  <span style={{
                                    fontSize:'0.75rem',
                                    fontWeight:700,
                                    color:'var(--gray-700)',
                                    background:'rgba(255,255,255,0.85)',
                                    border:`1px solid ${palette.border}`,
                                    borderRadius:'999px',
                                    padding:'4px 10px',
                                    whiteSpace:'nowrap',
                                  }}>
                                    Supplier {index + 1}
                                  </span>
                                </div>
                                <div className="file-preview-card">
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: (submission.artworkUrl && submission.artworkUrl.match(/\.(jpg|jpeg|png|webp)$/i)) && (submission.artworkData || (submission.artworkUrl && submission.artworkUrl.match(/\.pdf$/i) && !submission.artworkData)) ? '1fr 1fr' : '1fr',
                                    gap: 12,
                                    alignItems: 'start'
                                  }}>
                                    {/* Image preview */}
                                    {submission.artworkUrl && submission.artworkUrl.match(/\.(jpg|jpeg|png|webp)$/i) && (
                                      <div>
                                        <div className="file-preview-thumb">
                                          <img src={submission.artworkUrl} alt="artwork" />
                                        </div>
                                        <a href={submission.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                                          <ExternalLink size={14} /> Open full size
                                        </a>
                                      </div>
                                    )}

                                    {/* PDF data preview */}
                                    {submission.artworkData && (
                                      <div className="supplier-pdf-preview">
                                        <div className="file-preview-thumb is-doc">
                                          <FileText size={36} strokeWidth={1.5} />
                                          <p className="doc-title">PDF document</p>
                                          <p className="doc-sub">Click to view</p>
                                        </div>
                                        <button
                                          className="btn btn-secondary btn-sm supplier-pdf-open"
                                          onClick={() => {
                                            try {
                                              const blob = decodeBase64ToBlob(submission.artworkData, submission.artworkMimeType || 'application/pdf');
                                              const url = URL.createObjectURL(blob);
                                              const a = document.createElement('a');
                                              a.href = url;
                                              a.target = '_blank';
                                              a.rel = 'noopener noreferrer';
                                              document.body.appendChild(a);
                                              a.click();
                                              a.remove();
                                              setTimeout(() => URL.revokeObjectURL(url), 1500);
                                            } catch (error) {
                                              console.error('Error opening PDF:', error);
                                              toast.error('Could not open file: Invalid file data');
                                            }
                                          }}
                                        >
                                          <ExternalLink size={14} /> Open full size
                                        </button>
                                      </div>
                                    )}

                                    {/* PDF URL (legacy) */}
                                    {submission.artworkUrl && submission.artworkUrl.match(/\.pdf$/i) && !submission.artworkData && (
                                      <div className="supplier-pdf-preview">
                                        <div className="file-preview-thumb is-doc">
                                          <FileText size={36} strokeWidth={1.5} />
                                          <p className="doc-title">PDF document</p>
                                          <p className="doc-sub">Click to view</p>
                                        </div>
                                        <a href={submission.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm supplier-pdf-open">
                                          <ExternalLink size={14} /> Open full size
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                  {/* Per-submission review action for Stage 3 & Stage 4 */}
                                  {submissionCanApprove && !submissionAlreadyApproved && (stageNum === 3 || stageNum === 4) && (
                                    <div style={{marginTop:10}}>
                                      <button className="btn btn-primary btn-sm" onClick={() => setApprovalModal({ stage: stageNum, supplierId: submission.supplierId })}>
                                        <CheckCircle size={14} /> Review this submission
                                      </button>
                                    </div>
                                  )}

                                  {isAdmin && submission.status === 'completed' && (stageNum === 3 || stageNum === 4) && (
                                    <div style={{marginTop:10}}>
                                      <button
                                        className="btn btn-secondary btn-sm supplier-request-changes"
                                        onClick={() => setReopenModal({
                                          stageNum,
                                          supplierId: (submission.supplierId && submission.supplierId._id) || submission.supplierId,
                                          supplierName
                                        })}
                                      >
                                        <AlertTriangle size={14} /> Request changes
                                      </button>
                                    </div>
                                  )}
                                  {isAdmin && submission.reopenRequest?.requestedAt && (
                                    <button
                                      className="btn btn-ghost btn-sm"
                                      style={{marginTop:8}}
                                      onClick={() => setRevisionHistoryModal({
                                        supplierId: (submission.supplierId && submission.supplierId._id) || submission.supplierId,
                                        supplierName
                                      })}
                                    >
                                      <History size={14} /> Revision history
                                    </button>
                                  )}
                                  {submission.reopenRequest?.requestedAt && (
                                    <div style={{fontSize:'0.8125rem', color:'var(--danger)', marginTop:8}}>
                                      <strong>Change request:</strong> {submission.reopenRequest.reason}
                                      {submission.reopenRequest.resolvedAt ? ' (supplier re-uploaded)' : ''}
                                    </div>
                                  )}

                                  {/* Admin finalize action for Stage 4: upload scanned copy and mark complete */}
                                  {isAdmin && stageNum === 4 && (
                                    <div style={{marginTop:10}}>
                                      {submission.adminApproval && submission.adminApproval.uploadedAt ? (
                                        <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                          <div style={{fontSize:'0.85rem', color:'var(--gray-700)'}}>
                                            <strong>Final copy uploaded:</strong> {submission.adminApproval.uploaderName || 'Admin'} · {submission.adminApproval.uploadedAt ? formatDateTime(submission.adminApproval.uploadedAt) : ''}
                                          </div>
                                          {submission.adminApproval.artworkUrl ? (
                                            <div className="final-copy-panel">
                                              <div className="final-copy-preview">
                                                <img
                                                  src={submission.adminApproval.artworkUrl}
                                                  alt="Final copy uploaded"
                                                  className="final-copy-preview-image"
                                                />
                                              </div>
                                              <div className="final-copy-actions">
                                                <a href={submission.adminApproval.artworkUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                                                  <ExternalLink size={14} /> View uploaded file
                                                </a>
                                                <button
                                                  className="btn btn-secondary btn-sm"
                                                  onClick={() => setFinalizeModal({
                                                    supplierId: submission.supplierId,
                                                    isEdit: true,
                                                    existingFileName: submission.adminApproval.artworkFileName || 'existing final copy'
                                                  })}
                                                >
                                                  <Edit size={14} /> Edit final copy
                                                </button>
                                              </div>
                                            </div>
                                          ) : submission.adminApproval.artworkFileName ? (
                                            <div className="final-copy-panel">
                                              <div className="final-copy-file-card">
                                                <FileText size={24} color="var(--brand-red)" />
                                                <span>{submission.adminApproval.artworkFileName}</span>
                                              </div>
                                              <div className="final-copy-actions">
                                              <button className="btn btn-secondary btn-sm" onClick={() => {
                                                try {
                                                  const blob = decodeBase64ToBlob(submission.adminApproval.artworkData, submission.adminApproval.artworkMimeType || 'application/pdf');
                                                  const url = URL.createObjectURL(blob);
                                                  const a = document.createElement('a');
                                                  a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer'; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1500);
                                                } catch (err) { console.error(err); toast.error('Could not open file'); }
                                              }}>
                                                <ExternalLink size={14} /> View uploaded file
                                              </button>
                                              <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => setFinalizeModal({
                                                  supplierId: submission.supplierId,
                                                  isEdit: true,
                                                  existingFileName: submission.adminApproval.artworkFileName || 'existing final copy'
                                                })}
                                              >
                                                <Edit size={14} /> Edit final copy
                                              </button>
                                              </div>
                                            </div>
                                          ) : null}
                                        </div>
                                      ) : (
                                        <button className="btn btn-primary btn-sm" onClick={() => setFinalizeModal({ supplierId: submission.supplierId })}>
                                          <Upload size={14} /> Finalize & upload scanned copy
                                        </button>
                                      )}
                                    </div>
                                  )}

                                {/* Per-submission approval chain */}
                                {(stageNum === 3 || stageNum === 4) && (
                                  <div style={{marginTop:12}}>
                                    <p style={{fontWeight:600, marginBottom:8}}>Submission reviews</p>
                                    <div className="supplier-approval-grid" style={{marginBottom:16}}>
                                      {submissionApprovals.map((ap, ai) => (
                                        <ApprovalCard
                                          key={`${ap.role}-${ap.assignedUserId || ap.assignedUserEmail || ai}`}
                                          approval={ap}
                                          role={ap.role}
                                          currentUserRole={user?.role}
                                          stageData={submission}
                                          stageNum={stageNum}
                                          showReminderButton={isAdmin && submission.status !== 'completed' && shouldShowReminderAction(ap, submission, user?.role)}
                                          onSendReminder={() => handleSendReminderToApproval(stageNum, ap, submission.supplierId)}
                                          reminderSubmitting={reminderSubmittingApproval === `${stageNum}-${ap.role}-${ap.assignedUserId || ap.assignedUserEmail || 'unknown'}`}
                                          onClick={() => setViewChecklistModal({ approval: ap, role: ap.role, stageNum })}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}

                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Review Button - visible to all reviewers (Stage 1/2 only; Stage 3/4 use the per-submission button above) */}
                    {canApprove && !alreadyApproved && stageNum <= 2 && (
                      <div style={{marginBottom:20}}>
                        <button className="btn btn-primary btn-sm" onClick={() => setApprovalModal({ stage: stageNum })}>
                          <CheckCircle size={16} /> Review now
                        </button>
                      </div>
                    )}

                    {/* Upload panel for admin / supplier */}
                    {isAdmin && isCurrentStage && !stageData?.artworkUrl && !stageData?.artworkData && stageNum <= 2 && (
                      <div className="info-banner tone-warning" style={{marginBottom:20, flexDirection:'column'}}>
                        <div style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                          <UploadCloud size={18} color="var(--warning)" style={{marginTop:1}} />
                          <div>
                            <p className="info-banner-title">Upload required</p>
                            <p className="info-banner-body">
                              {stageNum === 1 ? 'Upload the initial artwork to begin Stage 1 review.' : 'NAFDAC approval received. Upload the final artwork to begin Stage 2 review.'}
                            </p>
                          </div>
                        </div>
                        <div style={{width:'100%'}}>
                          <ReuploadPanel artworkId={id} stageNum={stageNum} isSupplier={false} stageData={stageData} onSuccess={fetchArtwork} />
                        </div>
                      </div>
                    )}

                    {/* Changes requested resubmit */}
                    {isAdmin && isChanges && isCurrentStage && stageData?.artworkUrl && (
                      <div className="info-banner tone-danger" style={{marginBottom:20, flexDirection:'column'}}>
                        <div style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                          <AlertTriangle size={18} color="var(--danger)" style={{marginTop:1}} />
                          <div>
                            <p className="info-banner-title">Changes requested</p>
                            <p className="info-banner-body">
                              Please revise the artwork and re-upload to continue the approval process.
                            </p>
                          </div>
                        </div>
                        <div style={{width:'100%'}}>
                          <ReuploadPanel artworkId={id} stageNum={stageNum} isSupplier={false} stageData={stageData} onSuccess={fetchArtwork} />
                        </div>
                      </div>
                    )}

                    {/* Approvals */}
                    {!((stageNum === 3 || stageNum === 4) && supplierSubmissions.length > 0) && (
                      <div>
                        <p style={{fontWeight:600, fontSize:'0.875rem', marginBottom:12}}>Team reviews</p>

                        {/* Pending reviewer info */}
                        {isCurrentStage && stageData?.status === 'in_progress' && (
                          <div className="info-banner tone-info" style={{marginBottom:14}}>
                            <Info size={16} />
                            <p className="info-banner-body" style={{color:'var(--info)'}}>
                              <strong>Pending Review:</strong> {pendingReviewers.length > 0 ? pendingReviewers.join(', ') : 'No reviewer currently pending'}
                            </p>
                            <p className="info-banner-body" style={{color:'var(--info)', marginTop:4}}>
                            </p>
                          </div>
                        )}

                        {stageData?.status === 'completed' && pendingReminderApprovals.length > 0 && isAdmin && (
                          <div className="info-banner tone-info" style={{marginBottom:14}}>
                            <Info size={16} />
                            <div style={{display:'flex', flexDirection:'column', gap:8}}>
                              <p className="info-banner-body" style={{color:'var(--info)'}}>
                                This stage is complete, but one or more reviewer roles are still pending. Send a reminder to the pending reviewers.
                              </p>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleSendReminder(stageNum, assignedSupplierIds[0])}
                                disabled={reminderSubmittingStage === stageNum}
                                style={{alignSelf:'flex-start'}}>
                                {reminderSubmittingStage === stageNum ? <span className="spinner" /> : <><MousePointerClick size={14} /> Send reminder</>}
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="approval-grid" style={{marginBottom: 16}}>
                          {stageData?.approvals?.map((ap, index) => (
                            <ApprovalCard
                              key={`${ap.role}-${ap.assignedUserId || ap.assignedUserEmail || index}`}
                              approval={ap}
                              role={ap.role}
                              currentUserRole={user?.role}
                              stageData={stageData}
                              stageNum={stageNum}
                              supplierSubmissionsMissing={Boolean(missingSupplierIds && missingSupplierIds.length > 0)}
                              showReminderButton={isAdmin && stageData?.status === 'in_progress' && shouldShowReminderAction(ap, stageData, user?.role) && !((stageNum === 3 || stageNum === 4) && missingSupplierIds.length > 0)}
                              onSendReminder={() => handleSendReminderToApproval(stageNum, ap)}
                              reminderSubmitting={reminderSubmittingApproval === `${stageNum}-${ap.role}-${ap.assignedUserId || ap.assignedUserEmail || 'unknown'}`}
                              onClick={() => setViewChecklistModal({ approval: ap, role: ap.role, stageNum })}
                            />
                          ))}
                        </div>

                        {alreadyApproved && isCurrentStage && (
                          <div style={{display:'flex', alignItems:'center', gap:8, fontSize:'0.875rem', color:'var(--success)', fontWeight:600}}>
                            <CheckCircle size={16} /> You have approved this stage
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {approvalModal && (
        <ApprovalModal
          artwork={artwork}
          stageNum={approvalModal.stage || approvalModal}
          supplierId={approvalModal.supplierId}
          onClose={() => setApprovalModal(null)}
          onSubmit={handleApprovalSubmit}
        />
      )}

      {continueReviewModal && (
        <ContinueReviewModal
          stageNum={continueReviewModal.stage}
          supplierName={continueReviewModal.supplierName}
          onClose={() => setContinueReviewModal(null)}
          onContinue={() => {
            setApprovalModal({ stage: continueReviewModal.stage, supplierId: continueReviewModal.supplierId });
            setContinueReviewModal(null);
          }}
        />
      )}

      {finalizeModal && (
        <AdminFinalizeModal
          artworkId={id}
          supplierId={finalizeModal.supplierId}
          isEdit={finalizeModal.isEdit}
          existingFileName={finalizeModal.existingFileName}
          onClose={() => setFinalizeModal(null)}
          onSuccess={() => { setFinalizeModal(null); fetchArtwork(); }}
        />
      )}

      {reopenModal && (
        <ReopenSubmissionModal
          artworkId={id}
          {...reopenModal}
          onClose={() => setReopenModal(null)}
          onSuccess={() => { setReopenModal(null); fetchArtwork(); }}
        />
      )}

      {revisionHistoryModal && (
        <SubmissionRevisionHistoryModal
          artworkId={id}
          {...revisionHistoryModal}
          onClose={() => setRevisionHistoryModal(null)}
        />
      )}

      {viewChecklistModal && (
        <ViewChecklistModal
          approval={viewChecklistModal.approval}
          role={viewChecklistModal.role}
          stageNum={viewChecklistModal.stageNum}
          onClose={() => setViewChecklistModal(null)}
        />
      )}
    </div>
  );
}

function ContinueReviewModal({ stageNum, supplierName, onClose, onContinue }) {
  useModalLock();

  return (
    <div className="modal-overlay">
      <div className="modal modal-md">
        <div className="modal-header">
          <div>
            <h3 style={{fontSize:'1.0625rem', fontWeight:700}}>Continue to next supplier review?</h3>
            <p style={{fontSize:'0.875rem', color:'var(--gray-600)', marginTop:4}}>
              Another supplier submission remains for Stage {stageNum}. Review {supplierName}'s submission before leaving.
            </p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <p style={{margin:0, fontSize:'0.9rem', lineHeight:1.5}}>
            Would you like to open the next checklist now so you can continue reviewing the next supplier's artwork?
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>No, I will review later</button>
          <button className="btn btn-primary" onClick={onContinue}>
            <CheckCircle size={14} style={{marginRight:8}} /> Open next checklist
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminFinalizeModal({ artworkId, supplierId, isEdit = false, existingFileName = '', onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState('');
  const [deletePreviousCopy, setDeletePreviousCopy] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
  };

  const handleSubmit = async () => {
    if (!supplierId) return toast.error('Missing supplier');
    if (!file) return toast.error('Please attach the scanned copy (PDF or image)');
    if (isEdit && deletePreviousCopy === null) return toast.error('Please choose whether to delete or keep the former copy');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('supplierId', supplierId);
      fd.append('comment', comment || '');
      if (isEdit) fd.append('deletePreviousCopy', String(deletePreviousCopy));
      fd.append('file', file);
      const endpoint = `/artworks/${artworkId}/admin-finalize`;
      const method = isEdit ? 'put' : 'post';
      await api[method](endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(isEdit ? 'Final copy updated successfully' : 'Final approval uploaded and supplier notified');
      onSuccess && onSuccess();
    } catch (err) {
      console.error('admin finalize error', err);
      toast.error(err.response?.data?.message || 'Failed to finalize approval');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-md admin-finalize-modal">
        <div className="modal-header">
          <div>
            <div className="admin-finalize-kicker"><UploadCloud size={14} /> {isEdit ? 'Final copy revision' : 'Final approval'}</div>
            <h3 className="admin-finalize-title">{isEdit ? 'Edit final copy' : 'Finalize supplier approval and completion'}</h3>
            <p className="admin-finalize-subtitle">{isEdit ? 'Replace the uploaded scanned approval copy.' : 'Upload the scanned approval copy to complete this supplier submission.'}</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {isEdit && (
            <div className="admin-finalize-existing">
              <div className="admin-finalize-existing-icon"><FileText size={19} /></div>
              <div>
                <span className="admin-finalize-existing-label">Current uploaded copy</span>
                <strong>{existingFileName || 'Existing final copy'}</strong>
              </div>
            </div>
          )}
          <div className="form-group admin-finalize-file-group">
            <label className="form-label">{isEdit ? 'Choose replacement file' : 'Scanned approval copy'}</label>
            <label className={`admin-finalize-dropzone ${file ? 'has-file' : ''}`}>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={handleFileChange}
            />
              {file ? (
                <>
                  <CheckCircle size={24} />
                  <strong>{file.name}</strong>
                  <span>{(file.size / 1024 / 1024).toFixed(2)} MB · Click to choose another file</span>
                </>
              ) : (
                <>
                  <UploadCloud size={26} />
                  <strong>Choose a PDF or image</strong>
                  <span>Click here to browse · PDF, JPG, PNG up to 20 MB</span>
                </>
              )}
            </label>
          </div>
          {isEdit && (
            <fieldset className="admin-finalize-delete-option" required>
              <legend>Former copy handling <span>*</span></legend>
              <label className="admin-finalize-choice">
                <input
                  type="radio"
                  name="deletePreviousCopy"
                  value="delete"
                  checked={deletePreviousCopy === true}
                  onChange={() => setDeletePreviousCopy(true)}
                  required
                />
                <span>
                  <strong>Delete the former copy from Cloudinary</strong>
                  <small>The old file will be removed after the replacement uploads successfully.</small>
                </span>
              </label>
              <label className="admin-finalize-choice">
                <input
                  type="radio"
                  name="deletePreviousCopy"
                  value="keep"
                  checked={deletePreviousCopy === false}
                  onChange={() => setDeletePreviousCopy(false)}
                  required
                />
                <span>
                  <strong>Keep the former copy in Cloudinary</strong>
                  <small>The old file will remain available after the replacement.</small>
                </span>
              </label>
            </fieldset>
          )}
          <div className="form-group admin-finalize-comment-group">
            <label className="form-label">Comment (optional)</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={4}
              className="form-input"
              placeholder="Add a note for the supplier or internal reference"
              style={{minHeight:'96px'}}
            />
          </div>
        </div>
        <div className="modal-footer" style={{paddingTop:20}}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <span className="spinner" /> : <><Upload size={14} style={{marginRight:8}} /> {isEdit ? 'Replace final copy' : 'Upload & Finalize'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReopenSubmissionModal({ artworkId, stageNum, supplierId, supplierName, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useModalLock();

  const handleSubmit = async () => {
    if (!reason.trim()) return toast.error('Enter a reason for requesting changes');
    setSubmitting(true);
    try {
      await api.post(`/artworks/${artworkId}/reopen-supplier-submission`, {
        stage: stageNum,
        supplierId,
        reason: reason.trim()
      });
      toast.success(`${supplierName}'s Stage ${stageNum} submission reopened`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not reopen submission');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-md reopen-modal">
        <div className="modal-header">
          <div className="reopen-modal-heading">
            <div className="reopen-modal-icon"><AlertTriangle size={19} /></div>
            <div>
              <div className="reopen-modal-kicker">Supplier correction</div>
              <h3>Request changes</h3>
              <p>{supplierName} <span>·</span> Stage {stageNum}</p>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {stageNum === 3 && (
            <div className="reopen-dependency-note">
              <Info size={17} />
              <p>Reopening Stage 3 also resets this supplier's Stage 4 review. They will complete Stage 4 review and final sign-off again after Stage 3 is approved.</p>
            </div>
          )}
          <label className="form-label reopen-reason-label" htmlFor="reopen-reason">Reason for changes <span>Required</span></label>
          <textarea
            id="reopen-reason"
            className="form-input reopen-reason-textarea"
            rows={4}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={2000}
            placeholder="Describe the correction needed"
            required
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || !reason.trim()}>
            {submitting ? <span className="spinner" /> : <><AlertTriangle size={14} /> Request changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubmissionRevisionHistoryModal({ artworkId, supplierId, supplierName, onClose }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  useModalLock();

  useEffect(() => {
    let active = true;
    api.get(`/artworks/${artworkId}/submission-revisions`, { params: { supplierId } })
      .then((response) => { if (active) setRevisions(response.data || []); })
      .catch(() => toast.error('Could not load submission history'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [artworkId, supplierId]);

  const openArchivedBuffer = (data, mimeType, fileName) => {
    try {
      const binary = atob(data);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
      const url = URL.createObjectURL(new Blob([bytes], { type: mimeType || 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'archived-submission';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      toast.error('Could not open archived file');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-md revision-history-modal">
        <div className="modal-header">
          <div className="revision-history-heading">
            <div className="revision-history-icon"><History size={19} /></div>
            <div>
              <div className="reopen-modal-kicker">Audit trail</div>
              <h3>Submission history</h3>
              <p>{supplierName}</p>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {loading ? <div className="revision-history-loading"><span className="spinner spinner-dark" /></div> : revisions.length === 0 ? <p className="revision-history-empty">No archived revisions.</p> : <div className="revision-history-list">{revisions.map((revision) => {
            const snapshot = revision.submissionSnapshot || {};
            const finalCopy = snapshot.adminApproval || {};
            return (
              <article key={revision._id} className="revision-history-entry">
                <div className="revision-history-entry-top">
                  <strong>{revision.action === 'invalidated_by_stage3_reopen' ? 'Stage 4 invalidated' : `Stage ${revision.stage} reopened`}</strong>
                  <span>{formatDateTime(revision.requestedAt)}</span>
                </div>
                <div className="revision-history-requester">
                  Requested by {revision.requestedByName || 'Admin'}
                </div>
                <p className="revision-history-reason">{revision.reason}</p>
                {(snapshot.artworkUrl || snapshot.artworkData || finalCopy.artworkUrl || finalCopy.artworkData) && (
                  <div className="revision-history-files">
                    {snapshot.artworkUrl && <a className="btn btn-secondary btn-sm" href={snapshot.artworkUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Supplier file</a>}
                    {!snapshot.artworkUrl && snapshot.artworkData && <button className="btn btn-secondary btn-sm" onClick={() => openArchivedBuffer(snapshot.artworkData, snapshot.artworkMimeType, snapshot.artworkFileName)}><FileText size={14} /> {snapshot.artworkFileName || 'Open supplier file'}</button>}
                    {finalCopy.artworkUrl && <a className="btn btn-secondary btn-sm" href={finalCopy.artworkUrl} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Final copy</a>}
                    {!finalCopy.artworkUrl && finalCopy.artworkData && <button className="btn btn-secondary btn-sm" onClick={() => openArchivedBuffer(finalCopy.artworkData, finalCopy.artworkMimeType, finalCopy.artworkFileName)}><FileText size={14} /> {finalCopy.artworkFileName || 'Open final copy'}</button>}
                  </div>
                )}
              </article>
            );
          })}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
